import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SG = `${BACKEND_URL}/api/smartgrid`;

const sg = axios.create({ baseURL: SG, timeout: 15000, withCredentials: true });

sg.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401) {
            window.dispatchEvent(new CustomEvent("auth:unauthorized"));
        }
        return Promise.reject(err);
    },
);

export const fetchSgStatus = () => sg.get("/status").then((r) => r.data);
// Live system graph for the 3D map page (nodes + typed feedback edges).
export const fetchSgSystemMap = () => sg.get("/system-map").then((r) => r.data);
export const fetchSgSituation = () => sg.get("/situation").then((r) => r.data);
export const fetchSgIntel = () => sg.get("/intel").then((r) => r.data);
export const fetchSgTwins = (top = 0) => sg.get("/twins", { params: top ? { top } : {} }).then((r) => r.data);
export const fetchSgDecisions = () => sg.get("/decisions").then((r) => r.data);
export const updateSgConfig = (cfg) => sg.put("/config", cfg).then((r) => r.data);
export const fetchSgHypotheses = () => sg.get("/hypotheses").then((r) => r.data);
export const fetchSgRisk = () => sg.get("/risk").then((r) => r.data);
export const fetchSgAutotune = () => sg.get("/autotune").then((r) => r.data);
export const fetchSgOnlineMl = () => sg.get("/online-ml").then((r) => r.data);
export const fetchSgBandit = () => sg.get("/bandit").then((r) => r.data);
export const fetchSgSummary = () => sg.get("/summary").then((r) => r.data);
export const generateSgSummary = () => sg.post("/summary/generate").then((r) => r.data);
export const applySgToLive = (params) => sg.post("/apply-to-live", params).then((r) => r.data);
export const fetchSgBotStatus = () => sg.get("/bot/status").then((r) => r.data);
export const fetchSgBotGrid = () => sg.get("/bot/grid").then((r) => r.data);
export const fetchSgBotOrders = (status = "open") => sg.get("/bot/orders", { params: { status } }).then((r) => r.data);
export const fetchSgBotTrades = (limit = 200) => sg.get("/bot/trades", { params: { limit } }).then((r) => r.data);
export const fetchSgBotPriceHistory = () => sg.get("/bot/price-history").then((r) => r.data);
export const fetchSgBotEvents = (limit = 200) => sg.get("/bot/events", { params: { limit } }).then((r) => r.data);
export const fetchSgGridDetail = (gridId) => sg.get(`/bot/grid/${gridId}`).then((r) => r.data);
export const fetchSgBotTelemetry = () => sg.get("/bot/telemetry").then((r) => r.data);
export const setSgBotKillSwitch = (on) => sg.post("/bot/kill-switch", { on }).then((r) => r.data);
export const fetchSgBotConfig = () => sg.get("/bot/config").then((r) => r.data);
export const updateSgBotConfig = (cfg) => sg.put("/bot/config", cfg).then((r) => r.data);
export const fetchSgStrategy = () => sg.get("/bot/strategy").then((r) => r.data);
export const updateSgStrategy = (cfg) => sg.put("/bot/strategy", cfg).then((r) => r.data);
export const sgExportTradesCsvUrl = () => `${SG}/bot/export/trades.csv`;
export const sgExportPnlDailyCsvUrl = (days = 90) => `${SG}/bot/export/pnl-daily.csv?days=${days}`;
export const sgBotStart = (mode) => sg.post("/bot/start", { mode }).then((r) => r.data);
export const sgBotStop = () => sg.post("/bot/stop").then((r) => r.data);
export const sgBotMode = (mode) => sg.post("/bot/mode", { mode }).then((r) => r.data);
export const sgBotNetwork = (use_testnet) => sg.post("/bot/network", { use_testnet }).then((r) => r.data);
// SGB trade history (own DB, sgb-* scoped)
export const fetchSgHistoryTrades = (params = {}) => sg.get("/bot/history/trades", { params }).then((r) => r.data);
export const fetchSgHistoryStats = (params = {}) => sg.get("/bot/history/stats", { params }).then((r) => r.data);
export const fetchSgHistoryIntegrity = (params = {}) => sg.get("/bot/history/integrity", { params }).then((r) => r.data);
export const sgExportHistoryCsvUrl = (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")).toString();
    return `${SG}/bot/history/export.csv${qs ? `?${qs}` : ""}`;
};

// SGB group sell + orphans
export const fetchSgGroupSellActive = () => sg.get("/bot/group-sell/active").then((r) => r.data);
export const previewSgGroupSell = (source, gridIds) => sg.post("/bot/group-sell/preview", { source, grid_ids: gridIds }).then((r) => r.data);
export const executeSgGroupSell = (payload) => sg.post("/bot/group-sell/execute", payload).then((r) => r.data);
export const fetchSgOrphans = () => sg.get("/bot/orphans").then((r) => r.data);
export const resolveSgOrphansManual = (gridIds, sellPrice) => sg.post("/bot/orphans/resolve-manual", { grid_ids: gridIds, sell_price: sellPrice }).then((r) => r.data);
export const fetchSgCombos = () => sg.get("/bot/combos").then((r) => r.data);
export const cancelSgCombo = (comboId) => sg.post(`/bot/combos/${comboId}/cancel`).then((r) => r.data);

// Binance leader-venue feed + cross-exchange intel + market-data recorder
export const fetchSgBinance = () => sg.get("/binance").then((r) => r.data);
export const fetchSgMacro = () => sg.get("/macro").then((r) => r.data);
export const fetchSgCrossx = () => sg.get("/crossx").then((r) => r.data);
export const fetchSgRecorder = () => sg.get("/recorder").then((r) => r.data);

// Fast-move / liq-burst guard config (auto-tune toggle + manual thresholds)
export const fetchSgGuards = () => sg.get("/guards").then((r) => r.data);
export const updateSgGuards = (body) => sg.post("/guards", body).then((r) => r.data);

// Supervisor-level protections & gates (per-strategy enable flags)
export const fetchSgStrategies = () => sg.get("/strategies").then((r) => r.data);
export const updateSgStrategies = (body) => sg.post("/strategies", body).then((r) => r.data);

// PAPER-MODE ONLY full data reset (refused by the backend in live mode)
export const resetSgPaper = (wipeLearning = true) =>
    sg.post("/bot/reset-paper", { confirm: "RESET", wipe_learning: wipeLearning }).then((r) => r.data);

export const fetchSgRegimeLeaderboard = () => sg.get("/regime-leaderboard").then((r) => r.data);
export const fetchSgActions = () => sg.get("/actions").then((r) => r.data);
export const fetchSgEquityComparison = () => sg.get("/equity-comparison").then((r) => r.data);
export const fetchSgAnalytics = () => sg.get("/analytics").then((r) => r.data);
export const fetchSgBlueprint = () => sg.get("/blueprint").then((r) => r.data);
export const runSgBlueprintReplay = (body) =>
    sg.post("/blueprint/replay", body, { timeout: 180000 }).then((r) => r.data);
export const fetchSgBlueprintRuns = (limit = 30) =>
    sg.get("/blueprint/runs", { params: { limit } }).then((r) => r.data);
export const fetchSgBlueprintRun = (runId) =>
    sg.get(`/blueprint/runs/${runId}`).then((r) => r.data);
export const fetchSgBlueprintPaper = () => sg.get("/blueprint/paper").then((r) => r.data);
export const startSgBlueprintPaper = (body) =>
    sg.post("/blueprint/paper/start", body).then((r) => r.data);
export const stopSgBlueprintPaper = () => sg.post("/blueprint/paper/stop").then((r) => r.data);
export const resetSgBlueprintPaper = () => sg.post("/blueprint/paper/reset").then((r) => r.data);
export const tuneSgBlueprintPaper = () =>
    sg.post("/blueprint/paper/tune", null, { timeout: 180000 }).then((r) => r.data);

// ---- Layered architecture (L0 data / L1 prediction / L2 controls / L3 strategies) ----
export const fetchSgLayers = () => sg.get("/layers").then((r) => r.data);
export const fetchSgStrategyModules = () => sg.get("/strategy-modules").then((r) => r.data);
export const fetchSgStrategyModulesSummary = () =>
    sg.get("/strategy-modules/summary").then((r) => r.data);
export const enableSgStrategyModule = (sid) =>
    sg.post(`/strategy-modules/${sid}/enable`).then((r) => r.data);
export const disableSgStrategyModule = (sid) =>
    sg.post(`/strategy-modules/${sid}/disable`).then((r) => r.data);
export const fetchSgStrategyModuleStats = (sid) =>
    sg.get(`/strategy-modules/${sid}/stats`).then((r) => r.data);
export const fetchSgStrategyModuleEquity = (sid) =>
    sg.get(`/strategy-modules/${sid}/equity`).then((r) => r.data);
export const fetchSgStrategyModuleTrades = (sid, limit = 50) =>
    sg.get(`/strategy-modules/${sid}/trades`, { params: { limit } }).then((r) => r.data);
export const fetchSgStrategyModuleInputs = (sid) =>
    sg.get(`/strategy-modules/${sid}/inputs`).then((r) => r.data);
export const setSgStrategyModuleInput = (sid, input, enabled) =>
    sg.post(`/strategy-modules/${sid}/inputs`, { input, enabled }).then((r) => r.data);
export const fetchSgBusEvents = (topic, limit = 50) =>
    sg.get(`/events/${topic}`, { params: { limit } }).then((r) => r.data);
